import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Landmark, ShieldCheck } from "lucide-react";
import api, { formatPrice, mediaUrl, formatApiError, track } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";

const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"];

export default function Checkout() {
  const { cart, cartSubtotal } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shippingCfg, setShippingCfg] = useState({ price: 30, free_threshold: 2000, intl_price: 90, method: "Insured Express Courier", estimate: "2–5 business days" });
  const [payCfg, setPayCfg] = useState(null);
  const [method, setMethod] = useState(null);
  const [discount, setDiscount] = useState(null);
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const orderIdRef = useRef(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", suburb: "", state: "NSW", postcode: "", country: "Australia",
  });
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    api.get("/content/shipping").then((r) => { if (r.data?.data) setShippingCfg((c) => ({ ...c, ...r.data.data })); }).catch(() => {});
    api.get("/config/payments").then((r) => {
      setPayCfg(r.data);
      setMethod(r.data.paypal_enabled ? "paypal" : (r.data.bank_enabled ? "bank_transfer" : null));
    }).catch(() => {});
    track("checkout_started", { items: cart.length });
  }, []);

  useEffect(() => {
    if (user && user !== false) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  if (cart.length === 0) return (
    <div className="luxy-container py-24 text-center">
      <h1 className="font-serif text-3xl text-pitch">Your bag is empty</h1>
      <Link to="/shop" className="inline-block mt-6 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">Continue Shopping</Link>
    </div>
  );

  const isAU = form.country.toLowerCase() === "australia";
  const shipping = cartSubtotal >= shippingCfg.free_threshold && isAU ? 0 : (isAU ? shippingCfg.price : shippingCfg.intl_price);
  const discountAmt = discount?.amount || 0;
  const total = Math.max(0, cartSubtotal - discountAmt) + shipping;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyDiscount = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discounts/validate", { code, subtotal: cartSubtotal });
      setDiscount(data);
      toast.success(`Code ${data.code} applied`);
    } catch (e) { setDiscount(null); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };

  const validate = () => {
    const f = formRef.current;
    const required = ["name", "email", "phone", "address", "suburb", "postcode"];
    for (const k of required) if (!String(f[k] || "").trim()) return false;
    if (!/^\S+@\S+\.\S+$/.test(f.email)) return false;
    return true;
  };

  const buildPayload = (pm) => {
    const f = formRef.current;
    return {
      items: cart.map((i) => ({ product_id: i.product_id, quantity: 1 })),
      customer: { name: f.name, email: f.email, phone: f.phone },
      shipping_address: { address: f.address, suburb: f.suburb, state: f.state, postcode: f.postcode, country: f.country },
      discount_code: discount?.code || null,
      origin_url: window.location.origin,
      payment_method: pm,
    };
  };

  // --- PayPal handlers ---
  const createPaypalOrder = async () => {
    if (!validate()) { toast.error("Please complete your contact and shipping details first."); throw new Error("form_invalid"); }
    try {
      const { data } = await api.post("/checkout/create-order", buildPayload("paypal"));
      orderIdRef.current = data.order_id;
      return data.paypal_order_id;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      throw err;
    }
  };

  const onPaypalApprove = async (data) => {
    try {
      const { data: res } = await api.post("/checkout/paypal/capture", { order_id: orderIdRef.current, paypal_order_id: data.orderID });
      if (res.payment_status === "paid") {
        track("purchase_completed", { order_id: orderIdRef.current });
        navigate(`/payment/success?order_id=${orderIdRef.current}`);
      } else {
        toast.error("Your payment could not be confirmed. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const releaseCurrent = () => { if (orderIdRef.current) api.post(`/checkout/release/${orderIdRef.current}`).catch(() => {}); orderIdRef.current = null; };

  // --- Bank transfer handler ---
  const submitBank = async () => {
    if (!validate()) { toast.error("Please complete your contact and shipping details first."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout/create-order", buildPayload("bank_transfer"));
      track("order_placed_bank", { order_id: data.order_id });
      navigate(`/payment/success?order_id=${data.order_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  const input = "w-full border border-linen bg-white px-4 py-3 text-sm outline-none focus:border-pitch transition-colors";

  return (
    <div className="luxy-container py-12 sm:py-16">
      <h1 className="font-serif text-4xl text-pitch tracking-tight mb-10">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-10">
          {!user && (
            <div className="bg-bone p-4 text-sm flex items-center justify-between flex-wrap gap-2">
              <span className="text-charcoal/80">Checking out as a guest.</span>
              <Link to="/login" state={{ from: "/checkout" }} className="text-xs font-mono uppercase tracking-[0.15em] border-b border-pitch pb-0.5">Sign in</Link>
            </div>
          )}
          <div>
            <h2 className="eyebrow mb-5">Contact Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Full name" value={form.name} onChange={set("name")} className={input} data-testid="checkout-name" />
              <input required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={input} data-testid="checkout-email" />
              <input required placeholder="Phone" value={form.phone} onChange={set("phone")} className={`${input} sm:col-span-2`} data-testid="checkout-phone" />
            </div>
          </div>
          <div>
            <h2 className="eyebrow mb-5">Shipping Address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Street address" value={form.address} onChange={set("address")} className={`${input} sm:col-span-2`} data-testid="checkout-address" />
              <input required placeholder="Suburb / City" value={form.suburb} onChange={set("suburb")} className={input} data-testid="checkout-suburb" />
              {isAU ? (
                <select value={form.state} onChange={set("state")} className={input} data-testid="checkout-state">
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input placeholder="State / Province" value={form.state} onChange={set("state")} className={input} />
              )}
              <input required placeholder="Postcode" value={form.postcode} onChange={set("postcode")} className={input} data-testid="checkout-postcode" />
              <select value={form.country} onChange={set("country")} className={input} data-testid="checkout-country">
                <option>Australia</option><option>New Zealand</option><option>United States</option>
                <option>United Kingdom</option><option>Singapore</option><option>Other</option>
              </select>
            </div>
          </div>

          <div>
            <h2 className="eyebrow mb-5">Payment Method</h2>
            {!payCfg ? <div className="text-sm text-stonemuted">Loading payment options…</div> : (
              <div className="space-y-3">
                {payCfg.paypal_enabled && (
                  <button type="button" onClick={() => setMethod("paypal")} data-testid="pay-method-paypal"
                    className={`w-full flex items-center gap-3 border px-5 py-4 text-left transition-colors ${method === "paypal" ? "border-pitch bg-bone" : "border-linen hover:border-stonemuted"}`}>
                    <span className={`w-4 h-4 rounded-full border shrink-0 ${method === "paypal" ? "border-pitch bg-pitch" : "border-stonemuted"}`} />
                    <span className="font-medium">PayPal <span className="text-stonemuted font-normal">· Card or PayPal balance</span></span>
                  </button>
                )}
                {payCfg.bank_enabled && (
                  <button type="button" onClick={() => setMethod("bank_transfer")} data-testid="pay-method-bank"
                    className={`w-full flex items-center gap-3 border px-5 py-4 text-left transition-colors ${method === "bank_transfer" ? "border-pitch bg-bone" : "border-linen hover:border-stonemuted"}`}>
                    <span className={`w-4 h-4 rounded-full border shrink-0 ${method === "bank_transfer" ? "border-pitch bg-pitch" : "border-stonemuted"}`} />
                    <Landmark className="w-4 h-4 text-charcoal/70" />
                    <span className="font-medium">Bank Transfer <span className="text-stonemuted font-normal">· Direct deposit (AUD)</span></span>
                  </button>
                )}
                {!payCfg.paypal_enabled && !payCfg.bank_enabled && (
                  <div className="text-sm text-vermilion" data-testid="no-payment-methods">No payment methods are currently available. Please contact us to complete your purchase.</div>
                )}
                {method === "bank_transfer" && (
                  <p className="text-[12px] text-stonemuted leading-relaxed flex gap-2 pt-1">
                    <ShieldCheck className="w-4 h-4 shrink-0 text-charcoal/60" />
                    Your piece is reserved the moment you place the order. Transfer instructions are shown next and emailed to you. The item is released if payment is not received within the reservation window.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-bone p-8 h-fit lg:sticky lg:top-28">
          <h2 className="eyebrow mb-6">Order Summary</h2>
          <div className="space-y-4 max-h-64 overflow-y-auto mb-5">
            {cart.map((i) => (
              <div key={i.product_id} className="flex gap-3">
                <div className="w-14 h-16 bg-linen shrink-0 overflow-hidden">
                  {i.snapshot.image && <img src={mediaUrl(i.snapshot.image)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-stonemuted">{i.snapshot.brand}</div>
                  <div className="font-serif text-base leading-tight">{i.snapshot.title}</div>
                </div>
                <div className="text-sm">{formatPrice(i.snapshot.price, i.snapshot.currency)}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 border-t border-linen pt-5">
            <input placeholder="Discount code" value={code} onChange={(e) => setCode(e.target.value)}
              className="flex-1 border border-linen bg-white px-3 py-2 text-sm outline-none focus:border-pitch" data-testid="checkout-discount-code" />
            <button type="button" onClick={applyDiscount} disabled={applying} data-testid="checkout-apply-discount"
              className="text-xs font-mono uppercase tracking-[0.15em] border border-pitch px-4 hover:bg-pitch hover:text-ivory transition-colors disabled:opacity-50">Apply</button>
          </div>

          <div className="mt-6 space-y-2.5 text-sm">
            <div className="flex justify-between"><span className="text-stonemuted">Subtotal</span><span>{formatPrice(cartSubtotal)}</span></div>
            {discount && <div className="flex justify-between text-emeraldauth"><span>Discount ({discount.code})</span><span>−{formatPrice(discountAmt)}</span></div>}
            <div className="flex justify-between"><span className="text-stonemuted">Shipping</span><span>{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</span></div>
            <div className="flex justify-between text-base font-medium border-t border-linen pt-3 mt-1"><span>Total</span><span>{formatPrice(total)} AUD</span></div>
          </div>

          <div className="mt-6" data-testid="checkout-pay-area">
            {method === "paypal" && payCfg?.paypal_client_id && (
              <PayPalScriptProvider options={{ clientId: payCfg.paypal_client_id, currency: payCfg.currency || "AUD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "black", shape: "sharp", label: "pay" }}
                  createOrder={createPaypalOrder}
                  onApprove={onPaypalApprove}
                  onCancel={releaseCurrent}
                  onError={releaseCurrent}
                />
              </PayPalScriptProvider>
            )}
            {method === "bank_transfer" && (
              <button type="button" onClick={submitBank} disabled={submitting} data-testid="checkout-submit-bank"
                className="w-full bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] py-4 hover:bg-charcoal transition-colors disabled:opacity-50">
                {submitting ? "Reserving your piece…" : "Place Order · Pay by Bank Transfer"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-stonemuted mt-4 leading-relaxed">Payments are processed securely. LUXYREIGN never stores your card details. {shippingCfg.method} · {shippingCfg.estimate}.</p>
        </div>
      </div>
    </div>
  );
}
